import {
  IdCard,
  Target,
  Ruler,
  Stethoscope,
  Moon,
  Apple,
  Phone,
  type LucideIcon,
} from "lucide-react";
import type { StudentAnamnesisData } from "./types";

/**
 * Config do formulário de anamnese do aluno — espelho do formulário do trainer
 * no Kyra Desktop (`kyra-desktop/renderer/components/students/AnamnesisTab.tsx`),
 * SEM as seções "Avaliações" e "Notas livres" (campos do trainer).
 *
 * ATENÇÃO: os valores dos selects DEVEM bater 1:1 com os CHECK constraints da
 * tabela `beta_students` no Postgres — valor fora da lista quebra o save
 * (mesma regra documentada no AnamnesisTab do desktop).
 */

export type AnamneseFieldKey = keyof StudentAnamnesisData;

export type AnamneseFieldType =
  | "text"
  | "number"
  | "date"
  | "tel"
  | "textarea"
  | "select"
  | "boolean";

export interface AnamneseFieldDef {
  key: AnamneseFieldKey;
  type: AnamneseFieldType;
  options?: readonly string[];
  rows?: number;
  step?: string;
  placeholder?: string;
}

export interface AnamneseSectionDef {
  id: string;
  title: string;
  icon: LucideIcon;
  fields: AnamneseFieldDef[];
}

export const ANAMNESE_LABELS: Record<AnamneseFieldKey, string> = {
  age: "Idade",
  birth_date: "Data de nascimento",
  gender: "Gênero",
  profession: "Profissão",
  goal: "Objetivo",
  goal_details: "Detalhes do objetivo",
  training_experience: "Experiência com treino",
  training_frequency: "Frequência semanal de treino",
  preferred_training_type: "Tipo de treino preferido",
  weight: "Peso (kg)",
  height: "Altura (cm)",
  body_fat_percentage: "Percentual de gordura",
  medical_history: "Histórico médico",
  restrictions: "Restrições",
  current_medications: "Medicações atuais",
  allergies: "Alergias",
  previous_injuries: "Lesões prévias",
  chronic_diseases: "Doenças crônicas",
  surgeries: "Cirurgias",
  emergency_contact: "Contato de emergência",
  emergency_phone: "Telefone de emergência",
  physical_activity_level: "Nível de atividade física",
  sleep_quality: "Qualidade do sono",
  stress_level: "Nível de estresse",
  smoking: "Fumante",
  alcohol_consumption: "Consumo de álcool",
  dietary_restrictions: "Restrições alimentares",
  food_preferences: "Preferências alimentares",
  supplements: "Suplementos",
};

const GENDER_OPTIONS = ["Masculino", "Feminino", "Outro", "Prefiro não informar"] as const;
const TRAINING_EXPERIENCE_OPTIONS = [
  "Nenhuma",
  "Iniciante",
  "Intermediário",
  "Avançado",
  "Atleta",
] as const;
const PREFERRED_TRAINING_OPTIONS = [
  "Hipertrofia",
  "Resistência",
  "Performance",
  "Mobilidade",
  "Estética",
  "Reabilitação",
  "Outro",
] as const;
const ACTIVITY_LEVEL_OPTIONS = [
  "Sedentário",
  "Levemente Ativo",
  "Moderadamente Ativo",
  "Muito Ativo",
  "Extremamente Ativo",
] as const;
const SLEEP_QUALITY_OPTIONS = ["Muito Ruim", "Ruim", "Regular", "Boa", "Excelente"] as const;
const STRESS_OPTIONS = ["Muito Baixo", "Baixo", "Moderado", "Alto", "Muito Alto"] as const;
const ALCOHOL_OPTIONS = [
  "Nunca",
  "Raramente",
  "Socialmente",
  "Frequentemente",
  "Diariamente",
] as const;

export const ANAMNESE_SECTIONS: AnamneseSectionDef[] = [
  {
    id: "identification",
    title: "Identificação",
    icon: IdCard,
    fields: [
      { key: "age", type: "number", step: "1", placeholder: "Ex: 32" },
      { key: "birth_date", type: "date" },
      { key: "gender", type: "select", options: GENDER_OPTIONS },
      { key: "profession", type: "text", placeholder: "Ex: Designer" },
    ],
  },
  {
    id: "goal",
    title: "Objetivo",
    icon: Target,
    fields: [
      { key: "goal", type: "text", placeholder: "Ex: Ganhar massa magra" },
      { key: "goal_details", type: "text", placeholder: "Detalhes específicos do objetivo" },
      { key: "training_experience", type: "select", options: TRAINING_EXPERIENCE_OPTIONS },
      { key: "training_frequency", type: "number", step: "1", placeholder: "3" },
      { key: "preferred_training_type", type: "select", options: PREFERRED_TRAINING_OPTIONS },
    ],
  },
  {
    id: "composition",
    title: "Composição corporal",
    icon: Ruler,
    fields: [
      { key: "weight", type: "number", step: "0.1", placeholder: "75.5" },
      { key: "height", type: "number", step: "1", placeholder: "178" },
      { key: "body_fat_percentage", type: "number", step: "0.1", placeholder: "18.5" },
    ],
  },
  {
    id: "health",
    title: "Saúde",
    icon: Stethoscope,
    fields: [
      { key: "medical_history", type: "textarea", rows: 3 },
      { key: "restrictions", type: "textarea", rows: 2 },
      { key: "current_medications", type: "text" },
      { key: "allergies", type: "textarea", rows: 2 },
      { key: "previous_injuries", type: "textarea", rows: 2 },
      { key: "chronic_diseases", type: "textarea", rows: 2 },
      { key: "surgeries", type: "textarea", rows: 2 },
    ],
  },
  {
    id: "habits",
    title: "Hábitos",
    icon: Moon,
    fields: [
      { key: "physical_activity_level", type: "select", options: ACTIVITY_LEVEL_OPTIONS },
      { key: "sleep_quality", type: "select", options: SLEEP_QUALITY_OPTIONS },
      { key: "stress_level", type: "select", options: STRESS_OPTIONS },
      { key: "smoking", type: "boolean" },
      { key: "alcohol_consumption", type: "select", options: ALCOHOL_OPTIONS },
    ],
  },
  {
    id: "diet",
    title: "Alimentação",
    icon: Apple,
    fields: [
      { key: "dietary_restrictions", type: "textarea", rows: 2 },
      { key: "food_preferences", type: "textarea", rows: 2 },
      { key: "supplements", type: "text" },
    ],
  },
  {
    id: "emergency",
    title: "Emergência",
    icon: Phone,
    fields: [
      { key: "emergency_contact", type: "text", placeholder: "Nome completo" },
      { key: "emergency_phone", type: "tel", placeholder: "+55 11 98765-4321" },
    ],
  },
];

type AnyVal = StudentAnamnesisData[AnamneseFieldKey];

/**
 * Normaliza valor pra comparação de diff (mesma regra do desktop):
 * undefined/null/'' viram null, strings sofrem trim, números/booleans passam.
 */
export function normalizeAnamneseValue(v: AnyVal | undefined): AnyVal | null {
  if (v === undefined || v === null) return null;
  if (typeof v === "string") {
    const trimmed = v.trim();
    return trimmed === "" ? null : trimmed;
  }
  return v;
}

/** Diff draft vs base — só campos modificados entram no payload do POST. */
export function computeAnamneseDiff(
  draft: Partial<StudentAnamnesisData>,
  base: StudentAnamnesisData,
): Partial<StudentAnamnesisData> {
  const patch: Record<string, AnyVal | null> = {};
  const keys = Object.keys(ANAMNESE_LABELS) as AnamneseFieldKey[];
  for (const k of keys) {
    const a = normalizeAnamneseValue(draft[k] as AnyVal | undefined);
    const b = normalizeAnamneseValue(base[k] as AnyVal | undefined);
    if (a !== b) {
      patch[k] = a;
    }
  }
  return patch as Partial<StudentAnamnesisData>;
}
